import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { format, parseISO } from 'date-fns'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'

const emptyMatch = { match_date: '', opponent: '', result: '', started: true, minutes_played: 90, goals: 0, assists: 0, clean_sheet: false, rating: '', notes: '' }
const emptySeason = { club_name: '', league: '', country: '', season_label: '', contract_start: '', contract_end: '', salary: '' }

export default function Season({ session }) {
  const uid = session.user.id
  const [season, setSeason] = useState(null)
  const [matches, setMatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [showSeasonModal, setShowSeasonModal] = useState(false)
  const [showMatchModal, setShowMatchModal] = useState(false)
  const [seasonForm, setSeasonForm] = useState(emptySeason)
  const [matchForm, setMatchForm] = useState(emptyMatch)
  const [editingMatch, setEditingMatch] = useState(null)
  const [saving, setSaving] = useState(false)
  const [savedSeason, setSavedSeason] = useState(false)
  const [savedMatch, setSavedMatch] = useState(false)

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    const [s, m] = await Promise.all([
      supabase.from('seasons').select('*').eq('user_id', uid).eq('is_current', true).single(),
      supabase.from('matches').select('*').eq('user_id', uid).order('match_date', { ascending: false }),
    ])
    setSeason(s.data)
    setMatches(m.data || [])
    if (s.data) setSeasonForm(s.data)
    setLoading(false)
  }

  const saveSeason = async () => {
    setSaving(true)
    if (season) {
      await supabase.from('seasons').update(seasonForm).eq('id', season.id)
    } else {
      await supabase.from('seasons').insert({ ...seasonForm, user_id: uid, is_current: true })
    }
    await loadData()
    setSaving(false)
    setSavedSeason(true)
    setTimeout(() => {
      setShowSeasonModal(false)
      setSavedSeason(false)
    }, 700)
  }

  const saveMatch = async () => {
    setSaving(true)
    const payload = { ...matchForm, user_id: uid, season_id: season?.id, goals: Number(matchForm.goals), assists: Number(matchForm.assists), minutes_played: Number(matchForm.minutes_played), rating: matchForm.rating ? Number(matchForm.rating) : null }
    if (editingMatch) {
      await supabase.from('matches').update(payload).eq('id', editingMatch)
    } else {
      await supabase.from('matches').insert(payload)
    }
    await loadData()
    setSaving(false)
    setSavedMatch(true)
    setTimeout(() => {
      setShowMatchModal(false)
      setEditingMatch(null)
      setMatchForm(emptyMatch)
      setSavedMatch(false)
    }, 700)
  }

  const deleteMatch = async (id) => {
    if (!window.confirm('Delete this match?')) return
    await supabase.from('matches').delete().eq('id', id)
    setMatches(prev => prev.filter(m => m.id !== id))
  }

  const openEditMatch = (m) => {
    setMatchForm({ ...m, rating: m.rating || '' })
    setEditingMatch(m.id)
    setShowMatchModal(true)
  }

  const stats = {
    apps: matches.length,
    starts: matches.filter(m => m.started).length,
    goals: matches.reduce((a, m) => a + (m.goals || 0), 0),
    assists: matches.reduce((a, m) => a + (m.assists || 0), 0),
    minutes: matches.reduce((a, m) => a + (m.minutes_played || 0), 0),
    cleanSheets: matches.filter(m => m.clean_sheet).length,
    avgRating: matches.filter(m => m.rating).length > 0
      ? (matches.filter(m => m.rating).reduce((a, m) => a + m.rating, 0) / matches.filter(m => m.rating).length).toFixed(1)
      : '-',
  }

  const chartData = [...matches].reverse().slice(-10).map(m => ({
    name: m.opponent?.substring(0, 8) || '?',
    Goals: m.goals || 0,
    Assists: m.assists || 0,
    Rating: m.rating || 0,
  }))

  if (loading) return <div style={{ color: 'var(--muted)' }}>Loading...</div>

  return (
    <div>
      <div className="flex-between mb-32">
        <div>
          <div className="page-title">Season</div>
          <div className="page-sub">{season ? `${season.club_name} — ${season.season_label || 'Current Season'}` : 'Set up your current season'}</div>
        </div>
        <div className="flex gap-8">
          <button className="btn btn-ghost" onClick={() => { setSeasonForm(season || emptySeason); setShowSeasonModal(true) }}>
            {season ? 'Edit Season' : 'Set Up Season'}
          </button>
          {season && <button className="btn btn-primary" onClick={() => { setMatchForm(emptyMatch); setEditingMatch(null); setShowMatchModal(true) }}>+ Log Match</button>}
        </div>
      </div>

      {!season ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">⚽</div>
            <div className="empty-state-title">No season set up</div>
            <div className="empty-state-body">Add your current club and contract details to start tracking your season.</div>
            <button className="btn btn-primary" onClick={() => setShowSeasonModal(true)}>Set Up Season</button>
          </div>
        </div>
      ) : (
        <>
          {/* Season info */}
          <div className="card mb-24">
            <div className="grid-3 gap-24">
              <div>
                <div style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Club</div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{season.club_name}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>{season.league} · {season.country}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Contract</div>
                <div style={{ fontSize: 15, fontWeight: 500 }}>
                  {season.contract_start ? format(parseISO(season.contract_start), 'MMM yyyy') : '?'} — {season.contract_end ? format(parseISO(season.contract_end), 'MMM yyyy') : '?'}
                </div>
                {season.salary && <div style={{ fontSize: 13, color: 'var(--muted)' }}>{season.salary}</div>}
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Season</div>
                <div style={{ fontSize: 15, fontWeight: 500 }}>{season.season_label || '—'}</div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid-4 mb-24">
            {[
              { value: stats.apps, label: 'Appearances' },
              { value: stats.goals, label: 'Goals' },
              { value: stats.assists, label: 'Assists' },
              { value: stats.avgRating, label: 'Avg Rating' },
              { value: stats.starts, label: 'Starts' },
              { value: stats.minutes, label: 'Minutes' },
              { value: stats.cleanSheets, label: 'Clean Sheets' },
              { value: `${stats.goals + stats.assists}`, label: 'G+A' },
            ].map(s => (
              <div key={s.label} className="stat-card">
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Chart */}
          {chartData.length > 1 && (
            <div className="card mb-24">
              <div className="card-title">Goals & Assists — Last 10 Matches</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData}>
                  <XAxis dataKey="name" tick={{ fill: '#7A7860', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#7A7860', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: '#1A1A14', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 4, color: '#F5F3ED' }} />
                  <Bar dataKey="Goals" fill="#FDD700" radius={[2,2,0,0]} />
                  <Bar dataKey="Assists" fill="rgba(253,215,0,0.3)" radius={[2,2,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Match log */}
          <div className="card">
            <div className="card-title">Match Log</div>
            {matches.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-title">No matches logged</div>
                <div className="empty-state-body">Start logging your appearances to track your season performance.</div>
                <button className="btn btn-primary" onClick={() => setShowMatchModal(true)}>Log First Match</button>
              </div>
            ) : (
              <div>
                {matches.map(m => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 12, color: 'var(--muted)', minWidth: 40 }}>{m.match_date ? format(parseISO(m.match_date), 'dd MMM') : '—'}</div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>vs {m.opponent || '—'}</div>
                      <div style={{ fontSize: 13, color: 'var(--muted)' }}>{m.result || '—'}</div>
                      <div style={{ fontSize: 13, color: 'var(--muted)' }}>{m.minutes_played}'</div>
                      <span style={{ fontSize: 13, color: m.goals > 0 ? 'var(--gold)' : 'var(--muted)', fontWeight: m.goals > 0 ? 700 : 400 }}>{m.goals}G</span>
                      <span style={{ fontSize: 13, color: m.assists > 0 ? 'var(--gold)' : 'var(--muted)', fontWeight: m.assists > 0 ? 700 : 400 }}>{m.assists}A</span>
                      {m.rating && <span style={{ fontFamily: 'Bebas Neue', fontSize: 18, color: 'var(--gold)', letterSpacing: 1 }}>{m.rating}/5</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => openEditMatch(m)} className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }}>Edit</button>
                      <button onClick={() => deleteMatch(m.id)} className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Season Modal */}
      {showSeasonModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowSeasonModal(false)}>
          <div className="modal">
            <div className="modal-title">{season ? 'Edit Season' : 'Set Up Season'}</div>
            <div className="grid-2 mb-16">
              <div><label>Club Name</label><input value={seasonForm.club_name || ''} onChange={e => setSeasonForm(p => ({...p, club_name: e.target.value}))} placeholder="FC Example" /></div>
              <div><label>League</label><input value={seasonForm.league || ''} onChange={e => setSeasonForm(p => ({...p, league: e.target.value}))} placeholder="Liga X" /></div>
            </div>
            <div className="grid-2 mb-16">
              <div><label>Country</label><input value={seasonForm.country || ''} onChange={e => setSeasonForm(p => ({...p, country: e.target.value}))} placeholder="Poland" /></div>
              <div><label>Season Label</label><input value={seasonForm.season_label || ''} onChange={e => setSeasonForm(p => ({...p, season_label: e.target.value}))} placeholder="2025/26" /></div>
            </div>
            <div className="grid-2 mb-16">
              <div><label>Contract Start</label><input type="date" value={seasonForm.contract_start || ''} onChange={e => setSeasonForm(p => ({...p, contract_start: e.target.value}))} /></div>
              <div><label>Contract End</label><input type="date" value={seasonForm.contract_end || ''} onChange={e => setSeasonForm(p => ({...p, contract_end: e.target.value}))} /></div>
            </div>
            <div className="mb-24"><label>Salary (optional)</label><input value={seasonForm.salary || ''} onChange={e => setSeasonForm(p => ({...p, salary: e.target.value}))} placeholder="e.g. €1,500/month" /></div>
            <div className="flex gap-8">
              <button className="btn btn-primary" onClick={saveSeason} disabled={saving || savedSeason}>{saving ? <><span className="btn-spinner" /> Saving...</> : savedSeason ? '✓ Saved' : 'Save Season'}</button>
              <button className="btn btn-ghost" onClick={() => setShowSeasonModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Match Modal */}
      {showMatchModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowMatchModal(false)}>
          <div className="modal">
            <div className="modal-title">{editingMatch ? 'Edit Match' : 'Log Match'}</div>
            <div className="grid-2 mb-16">
              <div><label>Date</label><input type="date" value={matchForm.match_date || ''} onChange={e => setMatchForm(p => ({...p, match_date: e.target.value}))} /></div>
              <div><label>Opponent</label><input value={matchForm.opponent || ''} onChange={e => setMatchForm(p => ({...p, opponent: e.target.value}))} placeholder="FC Opponent" /></div>
            </div>
            <div className="grid-2 mb-16">
              <div><label>Result</label><input value={matchForm.result || ''} onChange={e => setMatchForm(p => ({...p, result: e.target.value}))} placeholder="2-1 W" /></div>
              <div><label>Minutes Played</label><input type="number" value={matchForm.minutes_played || ''} onChange={e => setMatchForm(p => ({...p, minutes_played: e.target.value}))} min={0} max={120} /></div>
            </div>
            <div className="grid-3 mb-16">
              <div><label>Goals</label><input type="number" value={matchForm.goals || 0} onChange={e => setMatchForm(p => ({...p, goals: e.target.value}))} min={0} /></div>
              <div><label>Assists</label><input type="number" value={matchForm.assists || 0} onChange={e => setMatchForm(p => ({...p, assists: e.target.value}))} min={0} /></div>
              <div><label>Rating (1-5)</label>
                <select value={matchForm.rating || ''} onChange={e => setMatchForm(p => ({...p, rating: e.target.value}))}>
                  <option value="">—</option>
                  {[1,2,3,4,5].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div className="grid-2 mb-16">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="started" checked={matchForm.started} onChange={e => setMatchForm(p => ({...p, started: e.target.checked}))} style={{ width: 'auto' }} />
                <label htmlFor="started" style={{ margin: 0 }}>Started</label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="cs" checked={matchForm.clean_sheet} onChange={e => setMatchForm(p => ({...p, clean_sheet: e.target.checked}))} style={{ width: 'auto' }} />
                <label htmlFor="cs" style={{ margin: 0 }}>Clean Sheet</label>
              </div>
            </div>
            <div className="mb-24"><label>Notes</label><textarea value={matchForm.notes || ''} onChange={e => setMatchForm(p => ({...p, notes: e.target.value}))} rows={2} placeholder="Performance notes..." /></div>
            <div className="flex gap-8">
              <button className="btn btn-primary" onClick={saveMatch} disabled={saving || savedMatch}>{saving ? <><span className="btn-spinner" /> Saving...</> : savedMatch ? '✓ Saved' : editingMatch ? 'Save Changes' : 'Log Match'}</button>
              <button className="btn btn-ghost" onClick={() => { setShowMatchModal(false); setEditingMatch(null); setMatchForm(emptyMatch) }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
